import offlineDb, { type OfflineDomain } from './offlineDb';

export type LegacyEncryptedStorageCategory = 'attendance' | 'workitems' | 'messages' | 'emails' | 'settings';

const LEGACY_ENCRYPTED_STORAGE_DB_NAME = 'TotalAccessEncryptedStorage';
const LEGACY_ENCRYPTED_STORAGE_STORE_NAME = 'encrypted_data';

const OFFLINE_DB_OPEN_TIMEOUT_MS = 8000;
const OFFLINE_DB_AUTO_CLEAR_FLAG = '__ta_offline_db_auto_cleared__';

const KNOWN_INDEXEDDB_NAMES = [
  'TotalAccessOfflineDB',
  'TotalAccessEncryptedStorage',
  'TotalAccessSecureStorage',
  'totalaccess_forms_offline',
  'totalaccess_email_local_cache',
  'CallRecordings',
];

export interface OfflineDbHealthCheckResult {
  healthy: boolean;
  totalEntries: number;
  tableCounts: {
    records: number;
    outbox: number;
    meta: number;
  };
  domainCounts: Partial<Record<OfflineDomain, number>>;
  error?: string;
}

function buildEmptyDomainCounts(): Partial<Record<OfflineDomain, number>> {
  return {
    workitems: 0,
    attendance: 0,
    messages: 0,
    emails: 0,
    'app-shell': 0,
    'offline-auth': 0,
  };
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'unknown error';
}

async function legacyEncryptedStorageExists(): Promise<boolean> {
  if (typeof window === 'undefined' || typeof indexedDB === 'undefined') {
    return false;
  }

  const indexedDbWithDatabases = indexedDB as IDBFactory & {
    databases?: () => Promise<Array<{ name?: string }>>;
  };

  if (typeof indexedDbWithDatabases.databases !== 'function') {
    return true;
  }

  try {
    const databases = await indexedDbWithDatabases.databases();
    return databases.some((database) => database.name === LEGACY_ENCRYPTED_STORAGE_DB_NAME);
  } catch {
    return true;
  }
}

async function openLegacyEncryptedStorageDb(): Promise<IDBDatabase | null> {
  if (!(await legacyEncryptedStorageExists())) {
    return null;
  }

  return await new Promise((resolve, reject) => {
    const request = indexedDB.open(LEGACY_ENCRYPTED_STORAGE_DB_NAME, 2);

    request.onerror = () => reject(request.error);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(LEGACY_ENCRYPTED_STORAGE_STORE_NAME)) {
        const store = db.createObjectStore(LEGACY_ENCRYPTED_STORAGE_STORE_NAME, { keyPath: 'key' });
        store.createIndex('category', 'category', { unique: false });
        store.createIndex('client_id', 'client_id', { unique: false });
        store.createIndex('category_client', ['category', 'client_id'], { unique: false });
        store.createIndex('expires_at', 'expires_at', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
  });
}

export async function healthCheckOfflineDb(): Promise<OfflineDbHealthCheckResult> {
  if (typeof window === 'undefined' || !offlineDb) {
    return {
      healthy: true,
      totalEntries: 0,
      tableCounts: { records: 0, outbox: 0, meta: 0 },
      domainCounts: buildEmptyDomainCounts(),
    };
  }

  try {
    await offlineDb.open();

    const [records, outbox, meta, recordDomains, outboxDomains, metaDomains] = await Promise.all([
      offlineDb.records.count(),
      offlineDb.outbox.count(),
      offlineDb.meta.count(),
      offlineDb.records.toArray(),
      offlineDb.outbox.toArray(),
      offlineDb.meta.toArray(),
    ]);

    const domainCounts = buildEmptyDomainCounts();

    for (const row of recordDomains) {
      domainCounts[row.domain] = (domainCounts[row.domain] || 0) + 1;
    }
    for (const row of outboxDomains) {
      domainCounts[row.domain] = (domainCounts[row.domain] || 0) + 1;
    }
    for (const row of metaDomains) {
      domainCounts[row.domain] = (domainCounts[row.domain] || 0) + 1;
    }

    return {
      healthy: true,
      totalEntries: records + outbox + meta,
      tableCounts: {
        records,
        outbox,
        meta,
      },
      domainCounts,
    };
  } catch (error) {
    return {
      healthy: false,
      totalEntries: 0,
      tableCounts: { records: 0, outbox: 0, meta: 0 },
      domainCounts: buildEmptyDomainCounts(),
      error: getErrorMessage(error),
    };
  }
}

export async function clearUnifiedOfflineDb(): Promise<void> {
  if (typeof window === 'undefined' || !offlineDb) {
    return;
  }

  const db = offlineDb;

  await db.transaction('rw', db.records, db.outbox, db.meta, db.publishQueue, async () => {
    await db.records.clear();
    await db.outbox.clear();
    await db.meta.clear();
    await db.publishQueue.clear();
  });
}

export async function clearLegacyEncryptedStorageCategory(
  category: LegacyEncryptedStorageCategory,
  clientId: number,
): Promise<number> {
  if (typeof window === 'undefined' || typeof indexedDB === 'undefined' || !clientId) {
    return 0;
  }

  const db = await openLegacyEncryptedStorageDb();
  if (!db) {
    return 0;
  }

  return await new Promise((resolve, reject) => {
    let removed = 0;

    const tx = db.transaction(LEGACY_ENCRYPTED_STORAGE_STORE_NAME, 'readwrite');
    const store = tx.objectStore(LEGACY_ENCRYPTED_STORAGE_STORE_NAME);
    const index = store.index('category_client');
    const request = index.openCursor(IDBKeyRange.only([category, clientId]));

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        return;
      }

      removed += 1;
      cursor.delete();
      cursor.continue();
    };

    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
    tx.oncomplete = () => {
      db.close();
      resolve(removed);
    };
  });
}

/**
 * Detect stale or corrupted offline DB by attempting to open it with a timeout.
 * If the DB fails to open (schema mismatch, corruption, blocked upgrade),
 * automatically delete all known IndexedDB databases and reload the page.
 *
 * This handles the scenario where an app update introduces a new DB schema
 * but the old data causes the Dexie open/upgrade to hang or fail.
 *
 * Uses a localStorage flag to prevent infinite reload loops — if the DB
 * still fails after a clear, we give up and let the app proceed.
 */
export async function detectAndClearStaleOfflineDb(): Promise<void> {
  if (typeof window === 'undefined' || !offlineDb) {
    return;
  }

  const alreadyAutoCleared = window.localStorage.getItem(OFFLINE_DB_AUTO_CLEAR_FLAG) === '1';

  try {
    await Promise.race([
      offlineDb.open(),
      new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error('Offline DB open timed out')),
          OFFLINE_DB_OPEN_TIMEOUT_MS,
        ),
      ),
    ]);
    // DB opened successfully — clear the auto-cleared flag if it was set
    if (alreadyAutoCleared) {
      window.localStorage.removeItem(OFFLINE_DB_AUTO_CLEAR_FLAG);
    }
  } catch (error) {
    console.error('[OfflineDb] Detected stale/corrupted offline DB:', error);

    if (alreadyAutoCleared) {
      console.warn('[OfflineDb] Auto-clear already attempted — skipping to avoid reload loop');
      return;
    }

    // Set flag before clearing so we don't loop
    try {
      window.localStorage.setItem(OFFLINE_DB_AUTO_CLEAR_FLAG, '1');
    } catch {
      // localStorage might be unavailable — proceed anyway
    }

    // Close any open DB connections before deleting
    try {
      offlineDb.close();
    } catch {
      // ignore
    }

    // Delete all known IndexedDB databases
    const deletePromises = KNOWN_INDEXEDDB_NAMES.map((dbName) =>
      new Promise<void>((resolve) => {
        try {
          const request = indexedDB.deleteDatabase(dbName);
          request.onsuccess = () => {
            console.log(`[OfflineDb] Auto-cleared IndexedDB: ${dbName}`);
            resolve();
          };
          request.onerror = () => {
            console.warn(`[OfflineDb] Failed to auto-clear IndexedDB: ${dbName}`);
            resolve();
          };
          request.onblocked = () => {
            console.warn(`[OfflineDb] Auto-clear blocked for IndexedDB: ${dbName}`);
            resolve();
          };
        } catch {
          resolve();
        }
      }),
    );

    await Promise.all(deletePromises);

    // Reload the page to reinitialize with a fresh DB
    console.log('[OfflineDb] Auto-clear complete — reloading page');
    window.location.reload();
  }
}
